import {SxProps, Theme} from '@mui/material';
import theme from 'src/theme';

/** The card every detail page opens with. */
export const cardSx: SxProps<Theme> = {
    background: theme.palette.common.white,
    borderRadius: '14px',
    border: '1px solid rgba(0, 0, 0, 0.05)',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05), 0 4px 12px rgba(0,0,0,0.04)',
    overflow: 'hidden',
};

export const headerSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    gap: 2.25,
    flexWrap: 'wrap',
    px: 3,
    py: 4,
    background: `linear-gradient(180deg, ${theme.palette.primaryColor.A50} 0%, ${theme.palette.common.white} 100%)`,
};

/** The logo as itself, on white, not cropped into a circle: it is a mark, not a portrait. */
export const logoSx: SxProps<Theme> = {
    width: '64px',
    height: '64px',
    borderRadius: '14px',
    background: theme.palette.common.white,
    border: `1px solid ${theme.palette.neutral[100]}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    overflow: 'hidden',
    '& img': {
        width: '100%',
        height: '100%',
        objectFit: 'contain',
    },
};

export const titleSx: SxProps<Theme> = {
    fontSize: '1.625rem',
    fontWeight: 600,
    letterSpacing: '-0.015em',
};

export const subtitleSx: SxProps<Theme> = {
    fontSize: '0.75rem',
    color: theme.palette.neutral[500],
    mt: 0.4,
};

/**
 * The four counters, as the card's facts.
 *
 * They were a block of their own below the form, all four wearing the same brand mark — so the icons
 * said nothing — and none of them led anywhere, though the tabs directly beneath list exactly these
 * locations, chargers and contracts.
 */
export const countersSx: SxProps<Theme> = {
    display: 'grid',
    gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)'},
    borderTop: `1px solid ${theme.palette.neutral[100]}`,
};

export const counterSx = (index: number, total: number): SxProps<Theme> => ({
    px: 3,
    py: 3,
    display: 'flex',
    flexDirection: 'column',
    gap: 1.25,
    textDecoration: 'none',
    color: 'inherit',
    borderRight: {xs: 'none', md: index < total - 1 ? `1px solid ${theme.palette.neutral[100]}` : 'none'},
    transition: 'background 150ms ease',
    '&:hover': {
        background: theme.palette.primaryColor.A50,
    },
});

export const counterHeadSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    gap: 1.1,
};

export const counterIconSx: SxProps<Theme> = {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    background: theme.palette.primaryColor.A50,
    border: `1px solid ${theme.palette.primaryColor.A100}`,
    color: theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    '& svg': {fontSize: '19px'},
};

export const counterLabelSx: SxProps<Theme> = {
    fontSize: '0.625rem',
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: theme.palette.neutral[500],
};

export const counterValueSx: SxProps<Theme> = {
    fontSize: '1.875rem',
    fontWeight: 600,
    letterSpacing: '-0.025em',
    lineHeight: 1,
    fontVariantNumeric: 'tabular-nums',
};

export const counterLinkSx: SxProps<Theme> = {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: theme.palette.primary.dark,
};

/** The form, under the tables now: a business's address is reference, not headline. */
export const factsSx: SxProps<Theme> = {
    display: 'grid',
    gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)'},
    borderTop: `1px solid ${theme.palette.neutral[100]}`,
};

export const factSx = (index: number, total: number): SxProps<Theme> => ({
    px: 3,
    py: 2.5,
    borderRight: {xs: 'none', sm: index % 2 === 0 ? `1px solid ${theme.palette.neutral[100]}` : 'none'},
    borderBottom: index < total - 2 ? `1px solid ${theme.palette.neutral[100]}` : 'none',
});

export const factLabelSx: SxProps<Theme> = {
    fontSize: '0.625rem',
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: theme.palette.neutral[500],
};

export const factValueSx: SxProps<Theme> = {
    fontSize: '0.875rem',
    fontWeight: 500,
    mt: 0.4,
    wordBreak: 'break-word',
};

export const factEmptySx: SxProps<Theme> = {
    fontSize: '0.875rem',
    fontWeight: 400,
    fontStyle: 'italic',
    color: theme.palette.neutral[400],
    mt: 0.4,
};
